import {
  LitElement,
  html,
  css,
  classMap,
  t,
  utils,
  paths,
  unsafeHTML,
} from '@hh/global.js'
const { addToCart, reRenderShopifySections } = utils

class HHCartMessage extends LitElement {
  static properties = {
    active: {
      type: Boolean,
      reflect: true,
    },
    productTitle: {
      type: String,
    },
    productLink: {
      type: String,
    },
    variantId: {
      type: String,
    },
    quantity: {
      type: Number,
    },
    type: {
      type: String,
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('cart-message:undo', this.onUndo.bind(this))
    window.addEventListener('cart-message:added', this.onAdded.bind(this))
    window.addEventListener(
      'cart-message:added-all',
      this.onAddedAll.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('cart-message:undo', this.onUndo)
    window.removeEventListener('cart-message:added', this.onAdded)
    window.removeEventListener('cart-message:added-all', this.onAddedAll)
  }

  onUndo(e) {
    const { detail } = e
    if (!detail) return

    this.active = true
    this.type = 'removed'

    const { title, link, variantId, quantity } = detail
    this.productTitle = title
    this.productLink = link
    this.variantId = variantId
    this.quantity = quantity
  }

  onAdded(e) {
    const { detail } = e
    if (!detail) return

    this.active = true
    this.type = 'added'

    const { title, link } = detail
    this.productTitle = title
    this.productLink = link
  }

  onAddedAll(e) {
    this.active = true
    this.type = 'added-all'
  }

  async reAddItem() {
    if (!this.variantId || !this.quantity) return

    setLoading(true)

    try {
      reRenderShopifySections(
        await addToCart(
          {
            id: this.variantId,
            quantity: this.quantity,
          },
          'inline-cart,header'
        )
      )

      window.dispatchEvent(
        new CustomEvent('cart-message:added', {
          bubbles: true,
          composed: true,
          detail: {
            title: this.productTitle,
            link: this.productLink,
          },
        })
      )
    } catch (e) {
      console.error({ error: e })
    } finally {
      setLoading(false)
    }
  }

  setLoading(value) {
    this.inlineCart && (this.inlineCart.loading = value)
    this.miniCart && (this.miniCart.loading = value)
  }

  removeTemplate() {
    if (this.type !== 'removed') return

    return html`
      ${this.productLinkTemplate()}
      <span class="message">${t('cart.general.removed_form_cart')}</span>
      <button class="action" @click=${this.reAddItem}>
        ${t('cart.general.undo')}
      </button>
    `
  }

  addedTemplate() {
    if (this.type !== 'added') return

    return html`
      <span class="message">
        ${t('cart.general.added_to_cart_html').split('[[product_title]]')[0]}
        ${this.productLinkTemplate()}
        ${unsafeHTML(
          t('cart.general.added_to_cart_html').split('[[product_title]]')[1]
        )}
      </span>
    `
  }

  addedAllTemplate() {
    if (this.type !== 'added-all') return

    return html`
      <span class="message"> ${t('cart.general.added_all_to_cart')} </span>
    `
  }

  productLinkTemplate() {
    return html`
      <span class="product-title">
        "<a class="link" href=${this.productLink}>${this.productTitle}</a>"
      </span>
    `
  }

  get inlineCart() {
    return document.querySelector('hh-inline-cart')
  }

  get miniCart() {
    return document.querySelector('hh-mini-cart')
  }

  render() {
    return html`
      <div
        role="alert"
        aria-live="polite"
        class=${classMap({
          wrapper: true,
          hide: !this.active,
        })}
      >
        <span class="icon">${unsafeHTML(paths.circleCheckmark)}</span>
        <span>
          ${this.removeTemplate()} ${this.addedTemplate()}
          ${this.addedAllTemplate()}
        </span>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .wrapper {
      background-color: var(--hh-color-brand-tertiary);
      padding: var(--hh-spacing-xs) var(--hh-spacing-xxs);
      border-left: 0.3rem solid var(--hh-color-success);
      width: 100%;
      margin: var(--hh-margin, 0 0 var(--hh-spacing-md));
      display: flex;
      align-items: flex-start;
      font: var(--hh-body-bold-xs-font);
    }

    @media (min-width: 1024px) {
      .wrapper {
        padding: var(--hh-spacing-sm) var(--hh-spacing-md);
        align-items: center;
        font: var(--hh-body-bold-sm-font);
      }
    }

    .icon {
      color: var(--hh-color-success);
      width: var(--hh-spacing-md);
      height: var(--hh-spacing-md);
      margin: 0 var(--hh-spacing-xs) 0 0;
    }

    .link,
    .action {
      color: var(--hh-color-text);
      text-decoration: underline;
    }

    .link:hover,
    .action:hover {
      color: var(--hh-color-brand-primary);
    }

    .action {
      appearance: none;
      padding: 0;
      border: none;
      background: transparent;
      font: inherit;
      cursor: pointer;
    }

    .hide {
      display: none;
    }
  `
}

customElements.define('hh-cart-message', HHCartMessage)
